import * as trace from 'owa-trace';
import IsPerformanceResourceSupported from './IsPerformanceResourceSupported';
import safePerformanceMark from './safePerformanceMark';
import { clearPerfMarkers } from './clearPerfMarkers';
import { hasQueryStringParameter } from 'owa-querystring';

/**
 * Prerequisites:
 * - This function expects a Performance Marker with the format "${measureName}_s" to exists (use the safePerformanceMarkStart to get this).
 * - If the start mark ("${measureName}_s") is not defined then undefined is returned.
 * - If there are multiple start marks in performance entries with same name then measure uses the most recently added start entry.
 *
 * Working:
 * - This function logs the end mark and returns the measure from start.
 *
 * @param measureName - measureName should be the same as the one used to mark the start measure, this name will be used in logging the measure in performance entries.
 * @param clearMarkers - defaults to true, this tells if we want to clear the start and end markers from performance entries. Send false here to see the markers in PAS results.
 */
export default function safePerformanceMarkEnd(measureName: string): PerformanceEntry {
    // Preliminary checks
    if (!IsPerformanceResourceSupported || !measureName || !isStartMarkerPresent(measureName)) {
        return undefined;
    }

    // Mark End
    safePerformanceMark(`${measureName}_e`);

    // Measure
    const isMeasureSuccess = measurePerfMark(measureName, `${measureName}_s`, `${measureName}_e`);

    // Get Measure
    let measure = undefined;
    if (isMeasureSuccess) {
        measure = getLastMarkedMeasure(measureName);
    }

    // Clear marks if there is
    if (!hasQueryStringParameter('cpm')) {
        clearPerfMarkers([`${measureName}_s`, `${measureName}_e`]);
    }

    // Return measure
    return measure;
}

/**
 * Returns if a start marker corresponding to the provided measureName is present or not
 * @param measureName
 */
function isStartMarkerPresent(measureName: string): boolean {
    let isStartMarkerPresent = false;
    try {
        const startMeasureName = `${measureName}_s`;
        const results = window?.performance?.getEntriesByName(startMeasureName);
        if (results?.length > 0) {
            isStartMarkerPresent = true;
        }
    } catch (e) {
        trace.errorThatWillCauseAlert(
            'IsStartMarkerPresentFailed: Failed to results for start marker',
            e
        );
    }
    return isStartMarkerPresent;
}

/**
 * Measures the timing from start to end and saves the result int the performance entries by measureName provided in the params
 * See https://w3c.github.io/user-timing/#dom-performance-measure for more info.
 * @param measureName name to be used when logging in perf entries
 * @param start start of the measure
 * @param end undefined by default, measures from the start of the
 */
function measurePerfMark(measureName: string, start: string, end: string): boolean {
    let measureDone = false;
    if (window?.performance?.measure) {
        try {
            window.performance.measure(measureName, start, end);
            measureDone = true;
        } catch (e) {
            // Start marker was not found: trace error
            trace.errorThatWillCauseAlert('MeasurePerfMarkersFailed: Failed to measure perf', e);
        }
    } else {
        trace.errorThatWillCauseAlert({
            name: 'MeasurePerfNotSupported',
            message: 'Failed to measure perf',
        });
    }
    return measureDone;
}

/**
 * Returns the most recently marked measure, this will be the last entry in the results.
 * @param measureName - string that identifies the measure name from the performance entries
 */
function getLastMarkedMeasure(measureName: string): PerformanceEntry {
    let lastMarkedMeasure;
    try {
        // Fetch the measure
        const results = window?.performance?.getEntriesByName(measureName);
        if (results?.length > 0) {
            // The measure will have been added in the last position of the array, so return the last entry from results
            lastMarkedMeasure = results.slice(-1)[0];
        }
    } catch (e) {
        trace.errorThatWillCauseAlert(
            'GetLastMarkedMeasureFailed: Failed to get last marked measure',
            e
        );
    }
    return lastMarkedMeasure;
}
